import { Plus, X } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

interface PolicyCondition {
  field: string;
  operator: 'equals' | 'contains' | 'matches' | 'gt' | 'lt';
  value: string;
}

interface PolicyAction {
  type: 'allow' | 'deny' | 'require_auth' | 'log' | 'notify';
  parameters?: Record<string, any>;
}

interface PolicyBuilderProps {
  onSave: (policy: {
    name: string;
    type: string;
    description: string;
    enabled: boolean;
    conditions: PolicyCondition[];
    actions: PolicyAction[];
  }) => void;
  onCancel: () => void;
}

const POLICY_FIELDS = [
  'package.name',
  'package.size',
  'package.visibility',
  'package.ecosystem',
  'user.role',
  'user.team',
  'request.ip',
  'request.method',
  'time.hour',
];

const OPERATORS = [
  { value: 'equals', label: 'equals' },
  { value: 'contains', label: 'contains' },
  { value: 'matches', label: 'matches (regex)' },
  { value: 'gt', label: 'greater than' },
  { value: 'lt', label: 'less than' },
];

const ACTION_TYPES = [
  { value: 'allow', label: 'Allow' },
  { value: 'deny', label: 'Deny' },
  { value: 'require_auth', label: 'Require Authentication' },
  { value: 'log', label: 'Log Event' },
  { value: 'notify', label: 'Send Notification' },
];

const POLICY_TEMPLATES = [
  {
    name: 'Require Auth for Private Packages',
    type: 'access',
    description: 'Ensure all private package access requires authentication',
    conditions: [
      {
        field: 'package.visibility',
        operator: 'equals' as const,
        value: 'private',
      },
    ],
    actions: [{ type: 'require_auth' as const }],
  },
  {
    name: 'Block Large Uploads',
    type: 'upload',
    description: 'Prevent uploads larger than 100MB',
    conditions: [{ field: 'package.size', operator: 'gt' as const, value: '104857600' }],
    actions: [{ type: 'deny' as const, parameters: { message: 'Package too large' } }],
  },
  {
    name: 'Security Scanning',
    type: 'security',
    description: 'Automatically scan all uploaded packages',
    conditions: [{ field: 'package.uploaded', operator: 'equals' as const, value: 'true' }],
    actions: [{ type: 'log' as const, parameters: { level: 'info' } }],
  },
];

export function PolicyBuilder({ onSave, onCancel }: PolicyBuilderProps) {
  const [name, setName] = useState('');
  const [type, setType] = useState('access');
  const [description, setDescription] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [conditions, setConditions] = useState<PolicyCondition[]>([
    { field: 'package.name', operator: 'equals', value: '' },
  ]);
  const [actions, setActions] = useState<PolicyAction[]>([{ type: 'allow' }]);

  const applyTemplate = (template: (typeof POLICY_TEMPLATES)[0]) => {
    setName(template.name);
    setType(template.type);
    setDescription(template.description);
    setConditions(template.conditions);
    setActions(template.actions);
  };

  const addCondition = () => {
    setConditions([...conditions, { field: 'package.name', operator: 'equals', value: '' }]);
  };

  const removeCondition = (index: number) => {
    setConditions(conditions.filter((_, i) => i !== index));
  };

  const updateCondition = (index: number, field: keyof PolicyCondition, value: string) => {
    const updated = [...conditions];
    updated[index] = { ...updated[index], [field]: value };
    setConditions(updated);
  };

  const addAction = () => {
    setActions([...actions, { type: 'allow' }]);
  };

  const removeAction = (index: number) => {
    setActions(actions.filter((_, i) => i !== index));
  };

  const updateAction = (index: number, field: keyof PolicyAction, value: string) => {
    const updated = [...actions];
    updated[index] = { ...updated[index], [field]: value };
    setActions(updated);
  };

  const handleSave = () => {
    if (!name.trim()) return;

    onSave({
      name: name.trim(),
      type,
      description: description.trim(),
      enabled,
      conditions: conditions.filter((c) => c.field && c.value),
      actions: actions.filter((a) => a.type),
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b p-6">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold">Create New Policy</h2>
            <Button variant="ghost" onClick={onCancel}>
              ×
            </Button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Templates */}
          <Card>
            <CardHeader>
              <CardTitle>Start from Template</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {POLICY_TEMPLATES.map((template, index) => (
                  <button
                    key={index}
                    onClick={() => applyTemplate(template)}
                    className="p-3 border border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors text-left"
                  >
                    <h4 className="font-medium text-sm">{template.name}</h4>
                    <p className="text-xs text-gray-600 mt-1">{template.description}</p>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle>Policy Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Policy Name</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter policy name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Policy Type</label>
                  <select
                    value={type}
                    onChange={(e) => setType(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="access">Access Control</option>
                    <option value="upload">Upload Policy</option>
                    <option value="download">Download Policy</option>
                    <option value="security">Security Policy</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  rows={3}
                  placeholder="Describe what this policy does"
                />
              </div>

              <div>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={enabled}
                    onChange={(e) => setEnabled(e.target.checked)}
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm font-medium text-gray-700">Enable policy</span>
                </label>
              </div>
            </CardContent>
          </Card>

          {/* Conditions */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Conditions</CardTitle>
                <Button size="sm" onClick={addCondition} className="flex items-center gap-1">
                  <Plus className="w-4 h-4" />
                  Add Condition
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {conditions.map((condition, index) => (
                <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <select
                    value={condition.field}
                    onChange={(e) => updateCondition(index, 'field', e.target.value)}
                    className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                  >
                    {POLICY_FIELDS.map((field) => (
                      <option key={field} value={field}>
                        {field}
                      </option>
                    ))}
                  </select>

                  <select
                    value={condition.operator}
                    onChange={(e) => updateCondition(index, 'operator', e.target.value as any)}
                    className="px-2 py-1 border border-gray-300 rounded text-sm"
                  >
                    {OPERATORS.map((op) => (
                      <option key={op.value} value={op.value}>
                        {op.label}
                      </option>
                    ))}
                  </select>

                  <input
                    type="text"
                    value={condition.value}
                    onChange={(e) => updateCondition(index, 'value', e.target.value)}
                    className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                    placeholder="Value"
                  />

                  {conditions.length > 1 && (
                    <Button size="sm" variant="ghost" onClick={() => removeCondition(index)} className="p-1 h-auto">
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Actions */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Actions</CardTitle>
                <Button size="sm" onClick={addAction} className="flex items-center gap-1">
                  <Plus className="w-4 h-4" />
                  Add Action
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {actions.map((action, index) => (
                <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <select
                    value={action.type}
                    onChange={(e) => updateAction(index, 'type', e.target.value as any)}
                    className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                  >
                    {ACTION_TYPES.map((actionType) => (
                      <option key={actionType.value} value={actionType.value}>
                        {actionType.label}
                      </option>
                    ))}
                  </select>

                  {(action.type === 'deny' || action.type === 'log' || action.type === 'notify') && (
                    <input
                      type="text"
                      placeholder="Parameters (JSON)"
                      className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                      onChange={(e) => {
                        try {
                          const params = e.target.value ? JSON.parse(e.target.value) : undefined;
                          updateAction(index, 'parameters', params);
                        } catch {
                          // Invalid JSON - ignore
                        }
                      }}
                    />
                  )}

                  {actions.length > 1 && (
                    <Button size="sm" variant="ghost" onClick={() => removeAction(index)} className="p-1 h-auto">
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        </div>

        <div className="sticky bottom-0 bg-white border-t p-6">
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={!name.trim()}>
              Create Policy
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
